import { create } from 'zustand';

/**
 * Whose portal is this, and what does it look like?
 *
 * The SPA is one build for every tenant, so nothing about the brand can be
 * baked in: not the title, not the favicon, not the colour, and certainly
 * not the name. Before anything renders, `load()` asks
 * `GET /api/tenant/resolve?host=<hostname>` — the one public endpoint that
 * exists for this — and the answer is painted onto the document (title,
 * favicon, the primary colour palette) and kept here for components.
 *
 * Fail soft, always. If the resolve call cannot be reached, the portal
 * renders with neutral defaults rather than a blank page: a customer who
 * cannot log in because the brand did not load has been failed twice.
 *
 * Deliberately does NOT import `services/api`: that module reads
 * `import.meta.env` at load time, which only Vite provides, and this store
 * is also loaded by `utils/format.js`, which the node check script runs
 * outside Vite. The API base is passed in by whoever calls `load()`.
 */

const DEFAULT_PRIMARY = '#f97316';
export const DEFAULT_LOCALE = 'en-US';
export const DEFAULT_CURRENCY = 'USD';

const DEFAULT_BRANDING = Object.freeze({
  companyName: 'Customer Portal',
  portalName: 'Customer Portal',
  title: 'Customer Portal',
  companyLogoUrl: '',
  faviconUrl: '',
  loginLogoUrl: '',
  loginBackgroundUrl: '',
  primaryColor: DEFAULT_PRIMARY,
  supportEmail: '',
  supportPhone: '',
  companyWebsite: '',
  poweredBy: true,
  locale: DEFAULT_LOCALE,
  currency: DEFAULT_CURRENCY,
  timezone: '',
});

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#rgb` or `#rrggbb` → [r, g, b], or null. */
function parseHex(hex) {
  if (typeof hex !== 'string' || !HEX.test(hex)) return null;
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

/**
 * A Tailwind-shaped palette from one colour.
 *
 * The lighter steps mix towards white and the darker towards black, at
 * ratios chosen so that the default orange reproduces Tailwind's own
 * `orange` scale closely enough that nobody notices — which is the test:
 * the default must look exactly as it always has, and only a tenant that
 * set a colour gets a new palette.
 */
const SHADE_MIX = {
  50: ['#ffffff', 0.95],
  100: ['#ffffff', 0.88],
  200: ['#ffffff', 0.72],
  300: ['#ffffff', 0.52],
  400: ['#ffffff', 0.28],
  500: [null, 0],
  600: ['#000000', 0.1],
  700: ['#000000', 0.25],
  800: ['#000000', 0.42],
  900: ['#000000', 0.55],
  950: ['#000000', 0.72],
};

function paletteFrom(hex) {
  const base = parseHex(hex);
  if (!base) return null;
  const out = {};
  for (const [shade, [towards, amount]] of Object.entries(SHADE_MIX)) {
    const target = towards ? parseHex(towards) : base;
    out[shade] = base.map((c, i) => Math.round(c + (target[i] - c) * amount));
  }
  return out;
}

/** Paint the branding onto the document: title, favicon, colour palette. */
function applyBranding(branding) {
  if (typeof document === 'undefined') return;
  try {
    if (branding.title) document.title = branding.title;

    if (branding.faviconUrl) {
      let link = document.querySelector('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = branding.faviconUrl;
      link.removeAttribute('type'); // the tenant's file may not be a PNG
    }

    const root = document.documentElement;
    root.style.setProperty('--brand-primary', branding.primaryColor || DEFAULT_PRIMARY);
    // The Tailwind `primary` scale reads these variables, with the stock
    // orange as its CSS defaults (styles/index.css). Only a tenant that chose
    // a colour overrides them, so the default build is byte-identical.
    if (branding.primaryColor && branding.primaryColor.toLowerCase() !== DEFAULT_PRIMARY) {
      const palette = paletteFrom(branding.primaryColor);
      if (palette) {
        for (const [shade, rgb] of Object.entries(palette)) {
          root.style.setProperty(`--primary-${shade}`, rgb.join(' '));
        }
      }
    } else {
      for (const shade of Object.keys(SHADE_MIX)) root.style.removeProperty(`--primary-${shade}`);
    }
  } catch {
    // A document we cannot write to is not a reason to stop rendering.
  }
}

export const useBrandingStore = create((set, get) => ({
  branding: DEFAULT_BRANDING,
  slug: null,
  apiBase: null,
  // A sandbox is a signable-in copy of another tenant (the operator console
  // makes one). The shell paints an undismissable bar from these, on the
  // login page and in the app — see components/common/SandboxBanner.jsx.
  sandbox: false,
  sandboxOf: null,
  // `unknown` is the server's own answer — "there is no active portal at
  // this address" — as opposed to `failed`, which is no answer at all. The
  // shell shows a page saying so for the first and the neutral login page
  // for the second: a login form on a hostname that names no tenant can
  // never sign anyone in, which is what the platform's bare apex looked
  // like once staging had two tenants (components/common/NoPortalHere.jsx).
  status: 'idle', // idle | loading | ready | failed | unknown
  // Goes up each time refresh() lands, so the shell can re-read the company's
  // look on top of the new one (DashboardLayout).
  revision: 0,

  /**
   * Resolve the brand for the hostname the browser is on. Once: a second
   * call while one is in flight, or after one has finished, is a no-op.
   * @param {string} apiBase - the API base URL (services/api's API_BASE_URL)
   */
  load: async (apiBase) => {
    if (get().status !== 'idle') return get().branding;
    set({ status: 'loading' });

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    // A slow resolve must not hold the login page hostage. Three seconds is
    // longer than any healthy answer and shorter than a person's patience.
    const timer = controller ? setTimeout(() => controller.abort(), 3000) : null;
    try {
      const host = typeof window !== 'undefined' ? window.location.hostname : '';
      const base = String(apiBase || '/api').replace(/\/+$/, '');
      const res = await fetch(`${base}/tenant/resolve?host=${encodeURIComponent(host)}`, {
        signal: controller?.signal,
        headers: { Accept: 'application/json' },
      });
      if (res.status === 404) {
        applyBranding(DEFAULT_BRANDING);
        set({ status: 'unknown' });
        return DEFAULT_BRANDING;
      }
      if (!res.ok) throw new Error(`resolve returned ${res.status}`);
      const data = await res.json();
      const branding = { ...DEFAULT_BRANDING, ...(data.branding || {}) };
      applyBranding(branding);
      if (typeof window !== 'undefined') window.__branding = branding; // read by utils/companyBranding.js
      set({
        branding,
        slug: data.slug ?? null,
        apiBase: data.apiBase ?? null,
        sandbox: data.sandbox === true,
        sandboxOf: data.sandbox === true ? data.sandboxOf ?? null : null,
        status: 'ready',
      });
      return branding;
    } catch (error) {
      // Neutral defaults, and say so in the console rather than nowhere.
      if (typeof console !== 'undefined') console.warn('Branding could not be resolved; using defaults.', error?.message);
      applyBranding(DEFAULT_BRANDING);
      set({ status: 'failed' });
      return DEFAULT_BRANDING;
    } finally {
      if (timer) clearTimeout(timer);
    }
  },

  /**
   * Read the brand again after it was saved. `no-cache`, since the answer carries
   * max-age=60 and the browser would otherwise hand back the one it has. A
   * failed read leaves the page looking as it does.
   */
  refresh: async (apiBase) => {
    try {
      const host = typeof window !== 'undefined' ? window.location.hostname : '';
      const base = String(apiBase || '/api').replace(/\/+$/, '');
      const res = await fetch(`${base}/tenant/resolve?host=${encodeURIComponent(host)}`, {
        cache: 'no-cache',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`resolve returned ${res.status}`);
      const data = await res.json();
      const branding = { ...DEFAULT_BRANDING, ...(data.branding || {}) };
      applyBranding(branding);
      if (typeof window !== 'undefined') window.__branding = branding;
      set({ branding, revision: get().revision + 1 });
    } catch (error) {
      if (typeof console !== 'undefined') console.warn('Branding could not be refreshed.', error?.message);
    }
  },

  /** For tests and previews: set the branding without a network call. */
  setBranding: (branding, { sandbox = false, sandboxOf = null } = {}) => {
    const merged = { ...DEFAULT_BRANDING, ...(branding || {}) };
    applyBranding(merged);
    if (typeof window !== 'undefined') window.__branding = merged;
    set({ branding: merged, sandbox: sandbox === true, sandboxOf: sandbox === true ? sandboxOf : null, status: 'ready' });
  },
}));

export default useBrandingStore;
